import { useEffect, useRef, useState } from "react";
import { useNow } from "../hooks/useNow";
import { useI18n } from "../i18n";
import { LinkButton } from "./ui";

/** Whole seconds left until `deadline`, never negative. */
export function secondsLeft(deadlineMs: number, nowMs: number): number {
  return Math.max(0, Math.ceil((deadlineMs - nowMs) / 1000));
}

/**
 * After a successful payment: return to the store automatically after the operator's delay, with a
 * way to stay. Deadline-based, so a tab that slept past it returns as soon as it wakes. With a
 * delay of 0 — or when the page was opened after the payment was already settled — only the
 * button is shown.
 */
export function RedirectCountdown({
  href,
  merchant,
  delaySec,
  auto,
}: {
  href: string;
  merchant: string;
  delaySec: number;
  /** Count down only when this page saw the payment arrive. */
  auto: boolean;
}) {
  const { t } = useI18n();
  const [deadline] = useState(() => Date.now() + delaySec * 1000);
  const [cancelled, setCancelled] = useState(false);
  const counting = auto && delaySec > 0 && !cancelled;
  const now = useNow(250, counting);
  const left = secondsLeft(deadline, now);
  const went = useRef(false);

  useEffect(() => {
    if (!counting || left > 0 || went.current) return;
    went.current = true;
    window.location.assign(href);
  }, [counting, left, href]);

  return (
    <div className="redirect">
      <LinkButton href={href} variant="primary" size="lg" block>
        {t("done.returnTo", { merchant })}
      </LinkButton>
      {auto && delaySec > 0 ? (
        <div className="redirect-row">
          {/* Announced once, not every second. */}
          <span className="sr-only" role="status">
            {cancelled ? t("redirect.cancelled") : t("redirect.announce", { merchant, seconds: delaySec })}
          </span>
          {counting ? (
            <>
              <span className="redirect-text num" aria-hidden="true">
                {t("redirect.in", { merchant, seconds: left })}
              </span>
              <button type="button" className="text-btn" onClick={() => setCancelled(true)}>
                {t("redirect.cancel")}
              </button>
            </>
          ) : (
            <span className="redirect-text" aria-hidden="true">
              {t("redirect.cancelled")}
            </span>
          )}
        </div>
      ) : null}
    </div>
  );
}
